// Module 09 §12 — per-dashboard access level. DEGRADED, never broken: a `viewer` still sees the
// whole dashboard, they just get no toolbar, no edit mode, no drag and no save.
//
// Ports `CustomDashboard.vue:150-154` + `:53-54`. Two details of that port are easy to get wrong:
//
//  1. **`level` sits on the GROUP, not the item.** `userAccessInfo.dashboards` is a list of grants,
//     each `{ level, items: [{ id, workspace_id }] }`; v3 finds the grant whose `items` contains
//     the dashboard id and reads `level` off the grant.
//  2. **The wire value for viewer-plus is `'viewer plus'` — with a space** (`:54`). Contract §12
//     names the levels `viewer | viewerPlus | editor`, which are the v4 TYPE names; writing the
//     camelCase form back as a comparison would silently grant every viewer-plus user full edit.
//
// No grant found → `editor`. That is v3's behaviour, not a fallback invented here: an owner of
// the workspace has no per-dashboard grant row, so `onlyDashboardPermission` is `undefined` and
// both `readOnly` and `viewerPlus` render false.
import type { UserAccessInfo } from '@/features/account'
import { useAccount } from '@/features/account'

/** v4 names for the three levels (contract §12). The WIRE spellings are normalised into these. */
export type DashboardAccessLevel = 'viewer' | 'viewerPlus' | 'editor'

export interface UseDashboardRbac {
  accessLevel: (dashboardId: number | string) => DashboardAccessLevel
  isViewer: (dashboardId: number | string) => boolean
  isViewerPlus: (dashboardId: number | string) => boolean
  isEditor: (dashboardId: number | string) => boolean
  /** Layout edits + save. `viewerPlus` may retune a widget's date/filters but not the layout. */
  canEdit: (dashboardId: number | string) => boolean
}

interface AccessGrant {
  level?: unknown
  items?: unknown
}

/** `'viewer plus'` / `'viewer_plus'` / `'Viewer Plus'` all mean the same grant. */
function normaliseLevel(level: unknown): DashboardAccessLevel {
  if (typeof level !== 'string') return 'editor'
  const flat = level
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/gu, '')
  if (flat === 'viewerplus') return 'viewerPlus'
  if (flat === 'viewer') return 'viewer'
  return 'editor'
}

function grantFor(access: UserAccessInfo, dashboardId: number | string): AccessGrant | null {
  for (const grant of (access.dashboards ?? []) as AccessGrant[]) {
    const items = grant?.items
    if (!Array.isArray(items)) continue
    const match = items.some(
      (item) => String((item as { id?: unknown }).id) === String(dashboardId),
    )
    if (match) return grant
  }
  return null
}

export function useDashboardRbac(): UseDashboardRbac {
  const account = useAccount()

  const accessLevel = (dashboardId: number | string): DashboardAccessLevel => {
    const grant = grantFor(account.userAccessInfo.value, dashboardId)
    return grant ? normaliseLevel(grant.level) : 'editor'
  }

  return {
    accessLevel,
    isViewer: (id) => accessLevel(id) === 'viewer',
    isViewerPlus: (id) => accessLevel(id) === 'viewerPlus',
    isEditor: (id) => accessLevel(id) === 'editor',
    canEdit: (id) => accessLevel(id) === 'editor',
  }
}
